import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Breadcrumbs,
  Link,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  MenuItem,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Tabs,
  Tab,
  Card,
  CardContent,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { runtimeProvidersAPI } from '../Api';

const defaultForm = {
  key: '',
  name: '',
  active: false,
  method_type: 'card',
  transportBaseUrl: '',
  transportPath: '/invoices',
  transportMethod: 'POST',
  transportTimeoutMs: 20000,
  authType: 'none',
  authHeaderName: 'Authorization',
  authToken: '',
  authApiKey: '',
  requestTemplateText: '{\n  "internalId": "{{input.orderId}}",\n  "amount": "{{pricing.totalAmount}}"\n}',
  responseSuccessPath: 'id',
  responseErrorPath: 'error',
  responseMapPaymentId: 'id',
  responseMapCard: 'dealRequisites.cardNumber',
  responseMapOwner: 'dealRequisites.fullName',
  responseMapBank: 'dealRequisites.bankName',
  responseMapLink: 'link',
  responseMapAmount: 'amount',
  responseMapTotalAmount: 'total_amount',
  responseMapExpiredAt: 'expireAt',
  responseMapAddress: 'address',
  responseMapCource: 'cource',
  responseMapSummCrypto: 'summ_crypto',
  retriesAttempts: 1,
  retriesDelayMs: 0,
  pricingComission: '',
  pricingRandomMin: '',
  pricingRandomMax: '',
  pricingDisableRandom: false,
  endpointsText: '[\n  {\n    "code": "create_invoice",\n    "name": "Create Invoice",\n    "path": "/v1/invoice",\n    "method": "POST",\n    "enabled": true\n  }\n]',
  paymentMethodsText: '[\n  {\n    "code": "card_main",\n    "label": "Card",\n    "type": "card",\n    "endpointCode": "create_invoice",\n    "priority": 0,\n    "active": true\n  }\n]',
  webhookText: '{\n  "enabled": false,\n  "path": "/cb/provider",\n  "eventPath": "event",\n  "statusPath": "status",\n  "orderIdPath": "order_id",\n  "paidStatuses": ["paid"],\n  "failedStatuses": ["failed", "expired"]\n}',
  pollingText: '{\n  "enabled": false,\n  "cron": "*/1 * * * *",\n  "endpointCode": "status",\n  "orderIdPath": "order_id",\n  "statusPath": "status",\n  "paidStatuses": ["paid"],\n  "failedStatuses": ["failed", "expired"]\n}',
};

export const RuntimeProviders = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [testPayload, setTestPayload] = useState({ amount: 1000, bank: '', orderId: '', methodType: 'card' });
  const [testResult, setTestResult] = useState(null);
  const [activeStep, setActiveStep] = useState(1);
  const [responseTab, setResponseTab] = useState('raw');

  const loadList = async () => {
    const res = await runtimeProvidersAPI.list();
    setItems(res.data?.data || []);
  };

  useEffect(() => {
    loadList();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm(defaultForm);
    setTestResult(null);
  };

  const mapFromEntity = (row) => ({
    key: row.key || '',
    name: row.name || '',
    active: !!row.active,
    method_type: row.method_type || 'card',
    transportBaseUrl: row.transport?.baseUrl || '',
    transportPath: row.transport?.path || '/invoices',
    transportMethod: row.transport?.method || 'POST',
    transportTimeoutMs: row.transport?.timeoutMs ?? 20000,
    authType: row.auth?.type || 'none',
    authHeaderName: row.auth?.headerName || 'Authorization',
    authToken: row.auth?.token || '',
    authApiKey: row.auth?.apiKey || '',
    requestTemplateText: JSON.stringify(row.requestTemplate || {}, null, 2),
    responseSuccessPath: row.responseMapping?.successPath || 'id',
    responseErrorPath: row.responseMapping?.errorPath || 'error',
    responseMapPaymentId: row.responseMapping?.fields?.payment_id || 'id',
    responseMapCard: row.responseMapping?.fields?.card || 'dealRequisites.cardNumber',
    responseMapOwner: row.responseMapping?.fields?.owner || 'dealRequisites.fullName',
    responseMapBank: row.responseMapping?.fields?.bank || 'dealRequisites.bankName',
    responseMapLink: row.responseMapping?.fields?.link || 'link',
    responseMapAmount: row.responseMapping?.fields?.amount || 'amount',
    responseMapTotalAmount: row.responseMapping?.fields?.total_amount || 'total_amount',
    responseMapExpiredAt: row.responseMapping?.fields?.expired_at || 'expireAt',
    responseMapAddress: row.responseMapping?.fields?.address || 'address',
    responseMapCource: row.responseMapping?.fields?.cource || 'cource',
    responseMapSummCrypto: row.responseMapping?.fields?.summ_crypto || 'summ_crypto',
    retriesAttempts: row.retries?.attempts ?? 1,
    retriesDelayMs: row.retries?.delayMs ?? 0,
    pricingComission: row.pricing?.comission ?? '',
    pricingRandomMin: row.pricing?.randomMin ?? '',
    pricingRandomMax: row.pricing?.randomMax ?? '',
    pricingDisableRandom: !!row.pricing?.disableRandom,
    endpointsText: JSON.stringify(row.endpoints || [], null, 2),
    paymentMethodsText: JSON.stringify(row.paymentMethods || [], null, 2),
    webhookText: JSON.stringify(row.webhook || {}, null, 2),
    pollingText: JSON.stringify(row.polling || {}, null, 2),
  });

  const buildPayload = () => {
    const parseJsonSafe = (text, fallback) => {
      try {
        return text ? JSON.parse(text) : fallback;
      } catch (_) {
        return fallback;
      }
    };

    let requestTemplate = {};
    try {
      requestTemplate = form.requestTemplateText ? JSON.parse(form.requestTemplateText) : {};
    } catch (e) {
      requestTemplate = {};
    }

    const endpoints = parseJsonSafe(form.endpointsText, []);
    const paymentMethods = parseJsonSafe(form.paymentMethodsText, []);
    const webhook = parseJsonSafe(form.webhookText, {});
    const polling = parseJsonSafe(form.pollingText, {});

    return {
      key: form.key,
      name: form.name,
      active: form.active,
      method_type: form.method_type,
      baseUrl: form.transportBaseUrl,
      transport: {
        baseUrl: form.transportBaseUrl,
        path: form.transportPath,
        method: form.transportMethod,
        timeoutMs: Number(form.transportTimeoutMs || 20000),
      },
      endpoints,
      paymentMethods,
      auth: {
        type: form.authType,
        headerName: form.authHeaderName,
        token: form.authToken || null,
        apiKey: form.authApiKey || null,
      },
      requestTemplate,
      responseMapping: {
        successPath: form.responseSuccessPath,
        errorPath: form.responseErrorPath,
        fields: {
          payment_id: form.responseMapPaymentId,
          card: form.responseMapCard,
          owner: form.responseMapOwner,
          bank: form.responseMapBank,
          link: form.responseMapLink,
          amount: form.responseMapAmount,
          total_amount: form.responseMapTotalAmount,
          expired_at: form.responseMapExpiredAt,
          address: form.responseMapAddress,
          cource: form.responseMapCource,
          summ_crypto: form.responseMapSummCrypto,
        },
      },
      retries: {
        attempts: Number(form.retriesAttempts || 1),
        delayMs: Number(form.retriesDelayMs || 0),
      },
      pricing: {
        comission: form.pricingComission === '' ? null : Number(form.pricingComission),
        randomMin: form.pricingRandomMin === '' ? null : Number(form.pricingRandomMin),
        randomMax: form.pricingRandomMax === '' ? null : Number(form.pricingRandomMax),
        disableRandom: !!form.pricingDisableRandom,
      },
      webhook,
      polling,
    };
  };

  const onSave = async () => {
    const payload = buildPayload();
    if (editingId) {
      await runtimeProvidersAPI.update(editingId, payload);
    } else {
      await runtimeProvidersAPI.create(payload);
    }
    await loadList();
    setOpen(false);
    resetForm();
  };

  const onEdit = (row) => {
    setEditingId(row._id);
    setForm(mapFromEntity(row));
    setOpen(true);
  };

  const onCreate = () => {
    resetForm();
    setOpen(true);
  };

  const onActivate = async (id) => {
    await runtimeProvidersAPI.activate(id);
    await loadList();
  };

  const onDelete = async (id) => {
    await runtimeProvidersAPI.remove(id);
    await loadList();
  };

  const onTest = async () => {
    if (!editingId) return;
    const res = await runtimeProvidersAPI.test(editingId, testPayload);
    setTestResult(res.data?.data || res.data);
  };

  const steps = ['Основные настройки', 'Запрос', 'Маппинг ответа', 'Тест и запуск'];

  const mappingRows = [
    ['payment_id', form.responseMapPaymentId],
    ['status', form.responseSuccessPath],
    ['amount', form.responseMapAmount],
    ['requisites.card', form.responseMapCard],
    ['requisites.owner', form.responseMapOwner],
    ['requisites.bank', form.responseMapBank],
    ['pay_url', form.responseMapLink],
    ['expires_at', form.responseMapExpiredAt],
  ];

  return (
    <Box sx={{ mt: 8, p: 4, boxShadow: 3, borderRadius: 2, bgcolor: '#fefcfb' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Runtime Providers</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate} sx={{ bgcolor: '#683d62', '&:hover': { bgcolor: '#5a3455' } }}>
          Добавить runtime provider
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row) => (
              <TableRow key={row._id}>
                <TableCell>{row.key}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.transport?.method}</TableCell>
                <TableCell><Chip size="small" label={row.method_type} /></TableCell>
                <TableCell>{row.active ? 'Да' : 'Нет'}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => onEdit(row)}>Редактировать</Button>
                  <Button size="small" onClick={() => onActivate(row._id)}>Активировать</Button>
                  <Button size="small" color="error" onClick={() => onDelete(row._id)}>Удалить</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xl">
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Breadcrumbs sx={{ mb: 0.5 }}>
                <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }} onClick={() => setOpen(false)}>
                  Процессинги
                </Link>
                <Typography color="text.primary">{editingId ? 'Редактирование' : 'Новый процессинг'}</Typography>
              </Breadcrumbs>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {form.name || 'Новый процессинг'}
                </Typography>
                <Chip size="small" color={form.active ? 'success' : 'default'} label={form.active ? 'Активный' : 'Черновик'} />
              </Stack>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button onClick={() => setOpen(false)}>Отмена</Button>
              <Button variant="outlined" onClick={onSave}>Сохранить</Button>
              <Button variant="contained" onClick={onSave} sx={{ bgcolor: '#6f4ef6', '&:hover': { bgcolor: '#5d3de0' } }}>
                Сохранить и активировать
              </Button>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map((label, idx) => (
              <Step key={label} onClick={() => setActiveStep(idx)}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr', gap: 2 }}>
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Настройки запроса</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 1.5 }}>
                  <TextField select label="Метод" value={form.transportMethod} onChange={(e) => setForm({ ...form, transportMethod: e.target.value })}>
                    <MenuItem value="POST">POST</MenuItem>
                    <MenuItem value="GET">GET</MenuItem>
                    <MenuItem value="PUT">PUT</MenuItem>
                  </TextField>
                  <TextField label="URL эндпоинт" value={form.transportPath} onChange={(e) => setForm({ ...form, transportPath: e.target.value })} />
                </Box>
                <TextField fullWidth sx={{ mt: 1.5 }} label="Базовый URL" value={form.transportBaseUrl} onChange={(e) => setForm({ ...form, transportBaseUrl: e.target.value })} />

                <Tabs value={activeStep} onChange={(_, v) => setActiveStep(v)} sx={{ mt: 1.5 }}>
                  <Tab label="Заголовки" value={0} />
                  <Tab label="Параметры" value={1} />
                  <Tab label="Тело запроса" value={2} />
                  <Tab label="Аутентификация" value={3} />
                </Tabs>

                <TextField
                  fullWidth
                  multiline
                  minRows={9}
                  sx={{ mt: 1.5 }}
                  label="Тело запроса (JSON)"
                  value={form.requestTemplateText}
                  onChange={(e) => setForm({ ...form, requestTemplateText: e.target.value })}
                />
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Тестовый ответ провайдера</Typography>
                <Tabs value={responseTab} onChange={(_, v) => setResponseTab(v)}>
                  <Tab value="raw" label="Raw ответ" />
                  <Tab value="mapped" label="Маппинг" />
                </Tabs>
                {responseTab === 'raw' ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={8}
                    margin="normal"
                    label="Raw response"
                    value={testResult?.raw ? JSON.stringify(testResult.raw, null, 2) : ''}
                    InputProps={{ readOnly: true }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    minRows={8}
                    margin="normal"
                    label="Mapped response"
                    value={testResult?.requisites ? JSON.stringify(testResult.requisites, null, 2) : ''}
                    InputProps={{ readOnly: true }}
                  />
                )}
                <Stack direction="row" spacing={1}>
                  <TextField size="small" type="number" label="Amount" value={testPayload.amount} onChange={(e) => setTestPayload({ ...testPayload, amount: Number(e.target.value) })} />
                  <TextField size="small" label="Bank" value={testPayload.bank} onChange={(e) => setTestPayload({ ...testPayload, bank: e.target.value })} />
                  <TextField size="small" select label="Method" value={testPayload.methodType} onChange={(e) => setTestPayload({ ...testPayload, methodType: e.target.value })}>
                    <MenuItem value="card">card</MenuItem>
                    <MenuItem value="cardsng">cardsng</MenuItem>
                    <MenuItem value="crypto">crypto</MenuItem>
                    <MenuItem value="p2purl">p2purl</MenuItem>
                  </TextField>
                  <TextField size="small" label="OrderId" value={testPayload.orderId} onChange={(e) => setTestPayload({ ...testPayload, orderId: e.target.value })} />
                  <Button startIcon={<PlayArrowIcon />} variant="outlined" onClick={onTest}>Отправить тест</Button>
                </Stack>
              </Paper>
            </Stack>

            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Маппинг ответа</Typography>
                <Alert severity="info" sx={{ mb: 1.5 }}>Сопоставьте поля ответа провайдера с вашей моделью.</Alert>
                <Stack spacing={1}>
                  {mappingRows.map(([target, source]) => (
                    <Box key={target} sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 1, alignItems: 'center' }}>
                      <TextField size="small" value={target} InputProps={{ readOnly: true }} />
                      <Typography sx={{ textAlign: 'center' }}>→</Typography>
                      <TextField size="small" value={source || ''} InputProps={{ readOnly: true }} />
                    </Box>
                  ))}
                </Stack>
              </Paper>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Информация о процессинге</Typography>
                  <Divider sx={{ my: 1 }} />
                  <TextField fullWidth size="small" label="Название" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={{ mb: 1 }} />
                  <TextField fullWidth size="small" label="Код" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} sx={{ mb: 1 }} />
                  <TextField fullWidth size="small" select label="Тип" value={form.method_type} onChange={(e) => setForm({ ...form, method_type: e.target.value })} sx={{ mb: 1 }}>
                    <MenuItem value="card">card</MenuItem>
                    <MenuItem value="cardsng">cardsng</MenuItem>
                    <MenuItem value="crypto">crypto</MenuItem>
                    <MenuItem value="p2purl">p2purl</MenuItem>
                  </TextField>
                  <TextField fullWidth size="small" type="number" label="Таймаут (мс)" value={form.transportTimeoutMs} onChange={(e) => setForm({ ...form, transportTimeoutMs: e.target.value })} sx={{ mb: 1 }} />
                  <TextField fullWidth size="small" type="number" label="Повторы" value={form.retriesAttempts} onChange={(e) => setForm({ ...form, retriesAttempts: e.target.value })} sx={{ mb: 1 }} />
                  <FormControlLabel control={<Switch checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />} label="Активный" />
                </CardContent>
              </Card>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Расширенная конфигурация</Typography>
                <Typography variant="caption" color="text.secondary">Endpoints JSON</Typography>
                <TextField fullWidth multiline minRows={6} margin="dense" value={form.endpointsText} onChange={(e) => setForm({ ...form, endpointsText: e.target.value })} />

                <Typography variant="caption" color="text.secondary">Payment methods JSON</Typography>
                <TextField fullWidth multiline minRows={6} margin="dense" value={form.paymentMethodsText} onChange={(e) => setForm({ ...form, paymentMethodsText: e.target.value })} />

                <Typography variant="caption" color="text.secondary">Webhook JSON</Typography>
                <TextField fullWidth multiline minRows={5} margin="dense" value={form.webhookText} onChange={(e) => setForm({ ...form, webhookText: e.target.value })} />

                <Typography variant="caption" color="text.secondary">Polling JSON</Typography>
                <TextField fullWidth multiline minRows={5} margin="dense" value={form.pollingText} onChange={(e) => setForm({ ...form, pollingText: e.target.value })} />
              </Paper>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions />
      </Dialog>
    </Box>
  );
};

export default RuntimeProviders;
